import type { ComponentProps } from 'react'
import { rem } from 'polished'
import styled from 'styled-components'

import BodyText from 'LuxKit/Typography/BodyText'
import BarCodeComponent from 'components/Pages/ExperienceVoucher/BarCode'
import { PrintStandardVoucher } from 'components/Pages/ExperienceVoucher/PrintStandardVoucher'

type Props = ComponentProps<typeof PrintStandardVoucher>

const SUPPORTED_BARCODE_FORMATS = new Set(['CODE128', 'CODE39'])

const BARCODE_SLOT_WIDTH = 300

const MiniBarcodes = styled.div`
  width: ${rem(BARCODE_SLOT_WIDTH)};
  max-width: ${rem(BARCODE_SLOT_WIDTH)};
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: ${rem(10)};
  margin-top: ${rem(4)};
`

const MiniBarcodeTicket = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${rem(4)};
  width: 100%;
  min-width: 0;

  @media print {
    break-inside: avoid;
    page-break-inside: avoid;
  }
`

const MiniBarcodeGraphic = styled.div`
  width: 100%;
  overflow: hidden;
  line-height: 0;

  /* JsBarcode sets explicit SVG width/height; scale uniformly into the slot. */
  & svg {
    display: block;
    max-width: 100% !important;
    width: 100% !important;
    height: auto !important;
  }
`

const MiniBarcodeValue = styled(BodyText)`
  font-size: ${rem(11)};
  line-height: 1.2;
  text-align: center;
  max-width: 100%;
  overflow-wrap: anywhere;
`

function IngressoMiniBarcodes({ barcodes }: { barcodes: NonNullable<App.ExperienceVoucher['barcodes']> }) {
  return (
    <MiniBarcodes data-testid="ingresso-voucher-barcodes">
      {barcodes.map((barcode, index) => (
        <MiniBarcodeTicket key={`${barcode.format}-${barcode.value}-${barcode.label ?? index}`}>
          <MiniBarcodeGraphic>
            <BarCodeComponent
              width={2}
              height={96}
              format={barcode.format}
              displayValue={false}
              value={barcode.value}
            />
          </MiniBarcodeGraphic>
          <MiniBarcodeValue variant="small" weight="bold">
            {barcode.value}
          </MiniBarcodeValue>
        </MiniBarcodeTicket>
      ))}
    </MiniBarcodes>
  )
}

export function PrintIngressoVoucher({ voucher, codeContent: _codeContent, ...props }: Props) {
  const customerName = [voucher.customer?.name, voucher.customer?.surname]
    .map(value => value?.trim())
    .filter(Boolean)
    .join(' ')
  const barcodes = voucher.barcodes?.filter((barcode) => (
    !!barcode.value.trim() && SUPPORTED_BARCODE_FORMATS.has(barcode.format)
  ))

  return (
    <PrintStandardVoucher
      {...props}
      voucher={voucher}
      customerName={customerName || undefined}
      codeContent={barcodes?.length ? <IngressoMiniBarcodes barcodes={barcodes} /> : undefined}
    />
  )
}
